/**
 * Read-only journey facts about a lesson, derived straight from its stored blob.
 *
 * `ReadingProgression` is the engine: constructing one runs the legacy-key migration and owns a
 * session. The lesson picker only wants to draw "★ 12/25" on six cards, and the menu only wants to
 * know whether a celebration is owed — neither should pay for an engine, let alone six of them. So
 * the pure derivations live here, and `ReadingProgression` uses the same functions rather than
 * keeping a second copy of the rules (the star formula has already been re-derived wrong once —
 * see the note on preRoundStats).
 */
import type { Pack, PackWord } from '@/data/packs/types';
import { wordInClass } from '@/data/packs/membership';
import { packKey } from '@/data/packs';
import { loadMastery, type WordStat } from './store';
import { MASTER_BOX, MIN_SESSIONS_TO_MASTER, STAR_COUNT, starsFor } from './config';

export interface LessonProgress {
  mastered: number;
  total: number;
  stars: number;              // 0..STAR_COUNT
  /** The journey was completed at some point. Stays true through star regression. */
  done: boolean;
  /** Completed, but the celebration screen still hasn't been shown. */
  celebrationPending: boolean;
}

/**
 * The lesson's journey universe. For banner-context lessons (Thai classifiers, JA/KO particles)
 * only the candidate tiles are ever shown and masterable — the bannered context words are not, so
 * `particles.ja` is a journey of 8 words even though the file holds far more. Sentence-order works
 * the same way: the sentence record drives the slot strip and never becomes a tile, so only the
 * `wort` tiles count. Deduped by id so a word reused across sets counts once.
 */
export function masterableWords(pool: Pack): PackWord[] {
  const all = pool.sets.flatMap((s) => s.words);
  const contextDriven = pool.mode === 'banner-context' || pool.mode === 'sentence-order';
  const masterable = (contextDriven && pool.candidateClass)
    ? all.filter((w) => wordInClass(w, pool.candidateClass!))
    : all;
  const seen = new Set<string>();
  return masterable.filter((w) => {
    const id = typeof w.id === 'string' ? w.id : w.text;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Box 5 held across enough distinct sessions that it can't have been crammed in one sitting. */
export function isMasteredStat(s: WordStat | undefined): boolean {
  if (!s || s.introducedAt === null) return false;
  return s.box >= MASTER_BOX && s.sessionsSeen >= MIN_SESSIONS_TO_MASTER;
}

/** One lesson's journey state, read from storage without touching it. */
export function lessonProgress(pack: Pack): LessonProgress {
  const words = masterableWords(pack);
  const data = loadMastery(packKey(pack));
  let mastered = 0;
  for (const w of words) {
    const id = typeof w.id === 'string' ? w.id : w.text;
    if (isMasteredStat(data.words[id])) mastered++;
  }
  const total = words.length;
  const { abcPlanetReachedAt, celebrationShownAt } = data.profile;
  return {
    mastered,
    total,
    stars: starsFor(mastered, total),
    done: abcPlanetReachedAt !== null,
    celebrationPending: abcPlanetReachedAt !== null && celebrationShownAt === null,
  };
}

export { STAR_COUNT };
